import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { renderTemplateBody } from '@/utils/whatsappTemplate';
import { CONVERSATION_MEDIA_ACCEPT } from '@/utils/conversationMedia';
import type { WhatsAppTemplate } from '@/types';

export interface TemplateSendPayload {
  templateName: string;
  language: string;
  parameters: string[];
}

interface MessageComposerProps {
  onSend: (content: string) => void;
  /** Dual-lane (#1584): internal note, not sent to the customer. */
  onSendInternal?: (content: string) => void;
  onSendFile?: (file: File, caption: string) => void;
  /** Required whenever `templateMode` can become true. */
  onSendTemplate?: (payload: TemplateSendPayload) => void;
  /**
   * The 24 h session window has closed: free text would be rejected by Meta, so
   * the composer offers approved templates instead (#1471).
   */
  templateMode?: boolean;
  templates?: WhatsAppTemplate[];
  templatesLoading?: boolean;
  templatesError?: boolean;
  /**
   * Placeholder values for the selected template, in order. Applied when the
   * template is chosen and again if they arrive while the fields are still
   * untouched. The sender can edit every value (#4106).
   */
  initialParameters?: readonly string[];
  onSelectedTemplateChange?: (template: WhatsAppTemplate | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function MessageComposer({
  onSend,
  onSendInternal,
  onSendFile,
  onSendTemplate,
  templateMode = false,
  templates,
  templatesLoading = false,
  templatesError = false,
  initialParameters,
  onSelectedTemplateChange,
  disabled,
  placeholder,
}: MessageComposerProps) {
  const { t } = useTranslation('messages');
  const [message, setMessage] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [parameters, setParameters] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editedRef = useRef(false);

  const selectedTemplate = useMemo(
    () => templates?.find((template) => template.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  );

  const selectedTemplateKey = selectedTemplate?.id ?? '';
  const seenTemplateKey = useRef(selectedTemplateKey);
  if (seenTemplateKey.current !== selectedTemplateKey) {
    seenTemplateKey.current = selectedTemplateKey;
    editedRef.current = false;
  }

  useEffect(() => {
    onSelectedTemplateChange?.(selectedTemplate);
  }, [onSelectedTemplateChange, selectedTemplate]);

  // Parameter fields follow the selected template, so switching templates never
  // carries a previous template's values into the wrong placeholder. A late
  // work-order prefill fills fields the sender has not edited yet.
  useEffect(() => {
    if (!selectedTemplate) {
      // Keep the unconditional reset. Bailing out when the list is already empty
      // changes commit timing for the open thread versus the conversation list.
      setParameters([]);
      return;
    }
    if (editedRef.current) return;
    const next = Array.from(
      { length: selectedTemplate.parameterCount },
      (_, index) => initialParameters?.[index] ?? '',
    );
    setParameters((prev) =>
      prev.length === next.length && prev.every((value, index) => value === next[index])
        ? prev
        : next,
    );
  }, [initialParameters, selectedTemplate]);

  const handleSend = () => {
    if (message.trim()) {
      onSend(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const allParametersFilled =
    selectedTemplate !== null && parameters.every((value) => value.trim().length > 0);

  const handleSendTemplate = () => {
    if (!selectedTemplate || !allParametersFilled || !onSendTemplate) return;

    onSendTemplate({
      templateName: selectedTemplate.name,
      language: selectedTemplate.language,
      parameters: parameters.map((value) => value.trim()),
    });
    setSelectedTemplateId('');
    setParameters([]);
  };

  if (templateMode) {
    return (
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          maxHeight: '55%',
          overflowY: 'auto',
        }}
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('sessionWindowClosedComposerHint')}
        </Alert>

        {templatesLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }} role="status" aria-label={t('templatesLoading')}>
            <CircularProgress size={24} />
          </Box>
        )}

        {templatesError && !templatesLoading && (
          <Alert severity="error">{t('templatesLoadFailed')}</Alert>
        )}

        {!templatesLoading && !templatesError && templates?.length === 0 && (
          <Alert severity="info">{t('noApprovedTemplates')}</Alert>
        )}

        {!templatesLoading && !templatesError && templates && templates.length > 0 && (
          <>
            <TextField
              select
              fullWidth
              size="small"
              label={t('templateLabel')}
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              disabled={disabled}
              sx={{ mb: 2 }}
            >
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name} ({template.language})
                </MenuItem>
              ))}
            </TextField>

            {selectedTemplate && (
              <>
                {initialParameters?.some((value) => value.trim().length > 0) && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {t('templateParametersFromWorkOrder')}
                  </Typography>
                )}
                {parameters.map((value, index) => (
                  <TextField
                    key={`${selectedTemplate.id}-${String(index)}`}
                    fullWidth
                    size="small"
                    label={t('templateParameter', { index: index + 1 })}
                    value={value}
                    onChange={(e) => {
                      editedRef.current = true;
                      const next = [...parameters];
                      next[index] = e.target.value;
                      setParameters(next);
                    }}
                    disabled={disabled}
                    sx={{ mb: 1.5 }}
                  />
                ))}

                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('templatePreview')}
                  </Typography>
                  {selectedTemplate.headerText && (
                    <Typography variant="body2" fontWeight={600}>
                      {selectedTemplate.headerText}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {renderTemplateBody(selectedTemplate.body, parameters)}
                  </Typography>
                  {selectedTemplate.footer && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedTemplate.footer}
                    </Typography>
                  )}
                </Paper>
              </>
            )}

            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSendTemplate}
              disabled={disabled || !allParametersFilled}
              fullWidth
            >
              {t('sendTemplate')}
            </Button>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {onSendFile && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept={CONVERSATION_MEDIA_ACCEPT}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                onSendFile(file, message.trim());
                setMessage('');
              }}
            />
            <Tooltip title={t('attach')}>
              <span>
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                  aria-label={t('attach')}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <AttachFileIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={placeholder ?? t('typeMessage')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />

        {onSendInternal && (
          <Button
            size="small"
            onClick={() => {
              if (!message.trim()) return;
              onSendInternal(message.trim());
              setMessage('');
            }}
            disabled={disabled || !message.trim()}
          >
            {t('sendInternal')}
          </Button>
        )}
        <Tooltip title={t('send')}>
          <span>
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={disabled || !message.trim()}
              aria-label={t('send')}
              sx={{
                minWidth: 44,
                minHeight: 44,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'action.disabledBackground',
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
